import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { useGlobalState } from '../global/context/global-context';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`city-tabpanel-${index}`}
      aria-labelledby={`city-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export function TheCityComponent() {
  const [tabValue, setTabValue] = useState(0);
  const { parametersPortal } = useGlobalState();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!parametersPortal) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </PageContainer>
    );
  }

  const tabs = [
    {
      label: 'História',
      icon: <HistoryIcon />,
      content: parametersPortal?.history,
      available: !!parametersPortal?.history,
    },
    {
      label: 'Hino',
      icon: <MusicNoteIcon />,
      content: parametersPortal?.hymnn,
      available: !!parametersPortal?.hymnn,
    },
    {
      label: 'Economia',
      icon: <AttachMoneyIcon />,
      content: parametersPortal?.economy,
      available: !!parametersPortal?.economy,
    },
    {
      label: 'Informações Geográficas',
      icon: <PublicIcon />,
      content: parametersPortal?.geographicInformation,
      available: !!parametersPortal?.geographicInformation,
    },
    {
      label: 'Institucional',
      icon: <AccountBalanceIcon />,
      content: parametersPortal?.institutional,
      available: !!parametersPortal?.institutional,
    },
  ];

  const availableTabs = tabs.filter(tab => tab.available);

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title="A Cidade"
          subtitle="Conheça mais sobre o município"
          breadcrumbs={[{ label: 'A Cidade' }]}
        />
        {availableTabs.length === 0 ? (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Informações sobre a cidade não disponíveis no momento.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="Informações da cidade"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 72,
                    textTransform: 'none',
                    fontSize: '1rem',
                  },
                }}
              >
                {availableTabs.map((tab, index) => (
                  <Tab
                    key={index}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                    id={`city-tab-${index}`}
                    aria-controls={`city-tabpanel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>

            {availableTabs.map((tab, index) => (
              <TabPanel key={index} value={tabValue} index={index}>
                <Box sx={{ px: { xs: 2, md: 4 } }}>
                  {tab.content ? (
                    <Box
                      sx={{
                        '& p': { mb: 2, lineHeight: 1.8 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          mt: 3,
                          mb: 2,
                          fontWeight: 600,
                        },
                        '& ul, & ol': {
                          pl: 3,
                          mb: 2,
                        },
                        '& li': {
                          mb: 1,
                        },
                        '& img': {
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: 1,
                          my: 2,
                        },
                        '& *': {
                          fontFamily: 'inherit !important',
                          fontSize: 'inherit !important',
                          color: 'inherit !important',
                          textAlign: 'inherit !important',
                          backgroundColor: 'transparent !important',
                        },
                        '& strong, & b': {
                          fontWeight: 'bold !important',
                        },
                        '& em, & i': {
                          fontStyle: 'italic !important',
                        },
                        '& u': {
                          textDecoration: 'underline !important',
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: tab.content }}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Conteúdo não disponível.
                    </Typography>
                  )}
                </Box>
              </TabPanel>
            ))}
          </Paper>
        )}
      </Container>
    </PageContainer>
  );
}
